import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {CashFlow} from "../../models/cash-flow";
import {CashFlowTemplateService} from "../../services/cash-flow-template.service";
import {CashFlowService} from "../../services/cash-flow.service";
import {FormBuilder, FormGroup, ValidationErrors, Validators} from "@angular/forms";
import {StatementSection} from "../../../tandem-core/models/statements/statement-section";
import {CurrencyService} from "../../../tandem-core/services/currency.service";
import {Timestamp} from "firebase/firestore";
import {DateService} from "../../../tandem-core/services/date.service";
import {DialogService, TandemDialogConfig} from "../../../tandem-core/services/dialog.service";
import {Router} from "@angular/router";
import {LocalStorageService} from "../../../tandem-core/services/local-storage.service";
import {FileUploadService} from "../../../tandem-core/services/file-upload.service";
import {StatementItem} from "../../../tandem-core/models/statements/statement-item";
import {TableConfiguration} from "../../../tandem-core/models/table-configuration";
import {CsvMappingComponent} from "../csv-mapping/csv-mapping.component";
import {DateValidationDialogComponent} from "../date-validation-dialog/date-validation-dialog.component";
import {StatementCategory} from "../../../tandem-core/models/statements/statement-category";
import {CoreService} from "../../../tandem-core/services/core.service";
import {Subscription} from "rxjs";
import {TandemUser} from "../../../auth/models/tandem-user";
import {HeaderService} from "../../../tandem-core/services/header.service";
import {DatePipe} from "@angular/common";
import {TandemDatePipe} from "../../../tandem-core/directives/tandem-date.pipe";

@Component({
  selector: 'tandem-cash-flow-form',
  templateUrl: './cash-flow-form.component.html',
  styleUrls: ['./cash-flow-form.component.scss']
})
export class CashFlowFormComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() public cashFlow: CashFlow = this.cashFlowTemplateService.generateDefaultCashFlow();
  @Input() public mode: 'create' | 'edit' = 'create';
  @Input() public cacheChanges = false;
  @Input() public user: TandemUser | null = null;


  public currentPageNumber: number = 0;
  public totalPages = 1;
  public dateRangeForm: FormGroup = new FormGroup<any>({});
  public activeSection: StatementSection | undefined;

  public uncategorizedRevenues: StatementItem[] = [];
  public uncategorizedExpenses: StatementItem[] = [];

  revenueMoveOptions: string[] = [];
  expenseMoveOptions: string[] = [];


  public uncategorizedRevenueConfig: TableConfiguration<StatementItem> = {
    subtitle: 'Uncategorized Revenue',
    collapsible: true,
    collapsed: false,
    draggable: false,
    columns: [
      {
        title: 'Name',
        columnType: 'text',
        itemProperty: 'name'
      },
      {
        title: 'Value',
        columnType: 'text',
        itemProperty: 'value',
        displayMask: t => this.currencyService.formatNumber(t.value) as string
      },
      {
        title: 'Date',
        columnType: 'text',
        itemProperty: 'date',
        displayMask: t => this.formatDate(t.date!.toDate()),
        sortable: true,
        sortedByDefault: true,
      },
    ],
    noDataMessage: 'No more uncategorized revenue.',
    hasCheckbox: true,
    dashedBorder: true,
  }
  public uncategorizedExpenseConfig: TableConfiguration<StatementItem> = {
    subtitle: 'Uncategorized Expenses',
    collapsible: true,
    collapsed: false,
    draggable: false,
    columns: [
      {
        title: 'Name',
        columnType: 'text',
        itemProperty: 'name'
      },
      {
        title: 'Value',
        columnType: 'text',
        itemProperty: 'value',
        displayMask: t => this.currencyService.formatNumber(t.value) as string
      },
      {
        title: 'Date',
        columnType: 'text',
        itemProperty: 'date',
        displayMask: t => this.formatDate(t.date!.toDate())
      },
    ],
    noDataMessage: 'No more uncategorized expenses.',
    hasCheckbox: true,
    dashedBorder: true,
  }

  resetSelectedRevEmitter: EventEmitter<void> = new EventEmitter<void>();
  resetSelectedExpEmitter: EventEmitter<void> = new EventEmitter<void>();

  @Output() public submitted: EventEmitter<CashFlow> = new EventEmitter<CashFlow>();
  selectedRevenues: StatementItem[] = [];
  selectedExpenses: StatementItem[] = [];

  coreSub: Subscription | undefined;

  @ViewChild('positionDate', {static: false})
  public positionDate?: TemplateRef<any>;

  constructor(private cashFlowService: CashFlowService,
              private cashFlowTemplateService: CashFlowTemplateService,
              private coreService: CoreService,
              private currencyService: CurrencyService,
              private dateService: DateService,
              private datePipe: TandemDatePipe,
              private dialog: DialogService,
              private fb: FormBuilder,
              private fileUploadService: FileUploadService,
              private dialogService: DialogService,
              private localStorageService: LocalStorageService,
              private router: Router,
              private cdr: ChangeDetectorRef,
              private header: HeaderService) {
  }

  ngAfterViewInit(): void {
    if (this.positionDate) {
      this.header.setTemplateRef(this.positionDate);
    }
  }

  ngOnInit(): void {
    this.totalPages = this.cashFlow.revenueSections.length + this.cashFlow.expenseSections.length + 1;
    this.revenueMoveOptions = this.cashFlow.revenueSections[0].categories.map(cat => cat.name);

    this.dateRangeForm = this.fb.group({
      startDate: [this.cashFlow.startDate.toDate().toISOString().substring(0, 10), Validators.required],
      endDate: [this.cashFlow.endDate.toDate().toISOString().substring(0, 10), Validators.required],
    }, {validators: this.dateRangeValidator(this.dateRangeForm)})
    this.dateRangeForm.valueChanges.subscribe(newVal => {
      this.cashFlow.startDate = this.dateService.getAdjustedTimestampFromDate(new Date(this.dateRangeForm.get('startDate')?.value));
      this.cashFlow.endDate = this.dateService.getAdjustedTimestampFromDate(new Date(this.dateRangeForm.get('endDate')?.value));
    })
    this.cashFlow.uncategorizedRevenue?.forEach(rev => {
      this.uncategorizedRevenues.push({name: rev.name, value: rev.value, date: Timestamp.fromMillis(1000 * (rev.date?.seconds || 0))})
    });
    this.cashFlow.uncategorizedExpenses?.forEach(exp => {
      this.uncategorizedExpenses.push({name: exp.name, value: exp.value, date: Timestamp.fromMillis(1000 * (exp.date?.seconds || 0))})
    });
    this.uncategorizedRevenues = [...this.uncategorizedRevenues];
    this.uncategorizedExpenses = [...this.uncategorizedExpenses];

    this.coreSub = this.coreService.dataChange$.subscribe(dataChanged => {
      this.cashFlow.revenueSections[0].categories.forEach(cat => {
        cat.items = [...cat.items];
      });
      this.cashFlow.revenueSections[0].categories = [...this.cashFlow.revenueSections[0].categories];

      this.cashFlow.expenseSections.forEach(section => {
        section.categories.forEach(cat => {
          cat.items = [...cat.items];
        });
        section.categories = [...section.categories];
      })
      this.recalculateTotals();
    })
    this.getPageLabel();
  }

  ngOnDestroy(): void {
    this.coreSub?.unsubscribe();
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      let names: string[] | null = this.localStorageService.getItem('uploadedTransactionNames');
      if (names?.includes(file.name)) {
        this.dialog.openConfirmDialog('Duplicate File Upload', `Looks like you've already uploaded this file, are you sure you'd like to upload these transactions again?`, 'Yes, Upload', `Don't Upload`).afterClosed().subscribe(confirmed => {
          if (confirmed) {
            this.processFileContents(file);
          }
        })
      } else {
        this.processFileContents(file);
      }
    }
    const fileInput = document.getElementById('hiddenFileInput') as HTMLInputElement;
    fileInput.value = ''
  }

  processFileContents(file: File) {
    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      const text = e.target?.result;
      if (typeof text === 'string') {
        const parsedOutput = this.fileUploadService.parseCSV(text);

        this.dialogService.openModal(CsvMappingComponent, { parsedOutput: parsedOutput }).afterClosed().subscribe(categories => {
          if (categories) {
            let extractedData: StatementItem[] = [];

            try {
              for (const row of parsedOutput.data) {
                let parsedValue: number | null = this.parseNumberFromString(row[categories.value]);

                if (parsedValue !== null) {
                  if (categories.invertAmounts) {
                    parsedValue = -parsedValue;
                  }

                  let item: StatementItem = {
                    name: (row[categories.name] || '').slice(0, 50),
                    value: Math.abs(parsedValue),
                    date: Timestamp.fromDate(new Date(row[categories.date]))
                  };

                  if (item.name && item.value && item.date) {
                    if (parsedValue < 0) {
                      this.uncategorizedExpenses.push(item);
                    } else {
                      this.uncategorizedRevenues.push(item);
                    }
                    extractedData.push(item);
                  }
                }
              }

              if (extractedData.length === 0) {
                this.dialog.openMessageDialog(
                  'Import Transactions Unsuccessful',
                  `No valid transactions were uploaded. Please check that the 'amount' column contains only numbers.`,
                  true
                );
              } else {
                this.cacheFileName(file.name);
                this.uncategorizedRevenues = [...this.uncategorizedRevenues];
                this.uncategorizedExpenses = [...this.uncategorizedExpenses];
                this.recalculateTotals();
              }

            } catch (e) {
              console.error("Error processing transactions:", e);
              this.dialog.openMessageDialog(
                'Import Transactions Unsuccessful',
                `An error occurred while processing your file. Please check the formatting and try again.`,
                true
              );
            }
          }
        });
      }
    };

    reader.readAsText(file);
  }


  parseNumberFromString(input: string | number): number | null {
    // If input is already a number, return it
    if (typeof input === 'number' && !isNaN(input)) {
      return input;
    }

    // Convert to string and trim spaces
    console.log(input);
    input = input.toString().trim();

    // Check for formats that should be ignored (e.g., dates)
    if (/\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(input)) {
      return null;
    }

    // Check for parentheses indicating negative numbers
    const isNegative = /\(([^)]+)\)/.test(input);

    // Regular expression to extract digits, decimal point, and negative sign
    const numberPattern = /-?\d+(\.\d+)?/;
    const match = input.match(numberPattern);

    if (match) {
      // Parse the number and apply the negative sign if needed
      let number = parseFloat(match[0]);

      // Ensure it's a valid number before returning
      if (isNaN(number)) {
        return null;
      }

      if (isNegative && number > 0) {
        number = -number;
      }

      return number;
    }

    return null;
  }

  // formatDate(date: Date) {
  //   let day = date.getDate().toString().padStart(2, '0');
  //   let month = (date.getMonth() + 1).toString().padStart(2, '0');
  //   let year = date.getFullYear().toString().substr(-2);
  //
  //   return `${month}/${day}/${year}`;
  // }

  triggerFileInput() {
    document.getElementById('hiddenFileInput')?.click();
  }

  public getPageLabel(): string {

    if (this.currentPageNumber <= this.cashFlow.revenueSections.length - 1) {
      this.activeSection = this.cashFlow.revenueSections[this.currentPageNumber]
    } else {
      this.activeSection = this.cashFlow.expenseSections[this.currentPageNumber - this.cashFlow.revenueSections.length];
    }

    if (this.currentPageNumber == this.totalPages) {
      this.activeSection = undefined;
    }

    return `${this.currentPageNumber + 1} of ${this.totalPages}`;
  }

  public setPage(pageNum: number): void {
    if (pageNum > 0 && pageNum <= this.cashFlow.expenseSections.length) {
      this.expenseMoveOptions = this.cashFlow.expenseSections[pageNum - 1].categories.map(cat => cat.name);
    }
    this.recalculateTotals();
    if (this.currentPageNumber - pageNum == 1 && this.currentPageNumber == 0) {

      let modalConfig: TandemDialogConfig = {
        title: 'Do you want to save first?',
        type: 'confirm',
        content: `It looks like you're about to exit the ${this.getTrackingToolName('single')}, do you want to save your work before leaving the page?`,
        actions: [
          {
            text: 'Cancel',
            role: 'cancel',
            callback: () => {
              this.dialogService.closeModal2();
            }
          },
          {
            text: 'No Thanks',
            role: 'confirm',
            callback: () => {
              this.dialogService.closeModal2();
              this.localStorageService.removeItem('createCashFlow');
              if (this.mode !== 'create') {
                this.router.navigate([`/cash-flows/${this.cashFlow.id}`]);
              } else {
                this.router.navigate(["/cash-flows"]);
              }
            }
          },
          {
            text: 'Save and Exit',
            role: 'primary',
            callback: () => {
              this.dialogService.closeModal2();
              this.onSubmit();
            }
          }
        ]
      }
      this.dialogService.openModal2(modalConfig);
    } else {
      this.recalculateTotals();
      this.currentPageNumber = pageNum;
      this.getPageLabel();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      if (this.currentPageNumber === 0) {
        this.revenueMoveOptions = this.cashFlow.revenueSections[0].categories.map(cat => cat.name);
      } else {
        // this.expenseMoveOptions = this.cashFlow.expenseSections[0].categories.map(cat => cat.name);
      }
    }
  }

  getTrackingToolName(variant: 'single' | 'plural') {
    if (variant === 'single') {
      return this.user?.customTTMetadata?.cashFlowName ? this.user.customTTMetadata.cashFlowName : 'Cash Flow';
    } else {
      return this.user?.customTTMetadata?.cashFlowNamePlural ? `${this.user.customTTMetadata.cashFlowNamePlural}` : 'Cash Flows';
    }
  }

  public onSubmit(): void {
    let newStartDate = this.dateService.getAdjustedTimestampFromDate(new Date(this.dateRangeForm.get('startDate')?.value));
    let newEndDate = this.dateService.getAdjustedTimestampFromDate(new Date(this.dateRangeForm.get('endDate')?.value));
    this.cashFlow.title = `${this.datePipe.transform(newStartDate.toDate())} to ${this.datePipe.transform(newEndDate.toDate())}`;

    let outlyingTransactions = [];

    this.cashFlow.revenueSections.forEach(sec => {
      sec.categories.forEach(cat => {
        cat.items.forEach(item => {
          let itemDate = new Date((item.date?.seconds || 0) * 1000);
          itemDate.setHours(0, 0, 0, 0); // Reset time to 00:00:00
          let startDate = new Date(newStartDate.seconds * 1000);
          startDate.setHours(0, 0, 0, 0); // Reset time to 00:00:00
          let endDate = new Date(newEndDate.seconds * 1000);
          endDate.setHours(0, 0, 0, 0); // Reset time to 00:00:00
          if (itemDate < startDate || itemDate > endDate) {
            outlyingTransactions.push(item);
          }
        });
      });
    });

    this.cashFlow.expenseSections.forEach(sec => {
      sec.categories.forEach(cat => {
        cat.items.forEach(item => {
          let itemDate = new Date((item.date?.seconds || 0) * 1000);
          itemDate.setHours(0, 0, 0, 0); // Reset time to 00:00:00
          let startDate = new Date(newStartDate.seconds * 1000);
          startDate.setHours(0, 0, 0, 0); // Reset time to 00:00:00
          let endDate = new Date(newEndDate.seconds * 1000);
          endDate.setHours(0, 0, 0, 0); // Reset time to 00:00:00
          if (itemDate < startDate || itemDate > endDate) {
            outlyingTransactions.push(item);
          }
        });
      });
    });
    if (outlyingTransactions.length > 0) {
      this.dialog.openModal(DateValidationDialogComponent, {outlyingTransactionsCount: outlyingTransactions.length}).afterClosed().subscribe(result => {
        if (result) {
          this.cashFlow.revenueSections.forEach(sec => {
            sec.categories.forEach(cat => {
              let startDate = new Date(newStartDate.seconds * 1000);
              startDate.setHours(0, 0, 0, 0); // Reset time to 00:00:00
              let endDate = new Date(newEndDate.seconds * 1000);
              endDate.setHours(0, 0, 0, 0); // Reset time to 00:00:00

              cat.items = cat.items.filter(item => {
                let itemDate = new Date((item.date?.seconds || 0) * 1000);
                itemDate.setHours(0, 0, 0, 0); // Reset time to 00:00:00

                if (itemDate >= startDate && itemDate <= endDate) {
                  return true; // Keep the item
                } else {
                  if (result === 'merge') {
                    item.date = (itemDate < startDate) ? newStartDate : newEndDate;
                    return true; // Keep and modify the item
                  }
                  // If not within date range and not merging, exclude the item
                  return false;
                }
              });
            });
          });
          this.cashFlow.expenseSections.forEach(sec => {
            sec.categories.forEach(cat => {
              let startDate = new Date(newStartDate.seconds * 1000);
              startDate.setHours(0, 0, 0, 0); // Reset time to 00:00:00
              let endDate = new Date(newEndDate.seconds * 1000);
              endDate.setHours(0, 0, 0, 0); // Reset time to 00:00:00

              cat.items = cat.items.filter(item => {
                let itemDate = new Date((item.date?.seconds || 0) * 1000);
                itemDate.setHours(0, 0, 0, 0); // Reset time to 00:00:00

                if (itemDate >= startDate && itemDate <= endDate) {
                  return true; // Keep the item
                } else {
                  if (result === 'merge') {
                    item.date = (itemDate < startDate) ? newStartDate : newEndDate;
                    return true; // Keep and modify the item
                  }
                  // If not within date range and not merging, exclude the item
                  return false;
                }
              });
            });
          });
          this.recalculateTotals();
          this.submitted.emit(this.cashFlow);
        }
      })
    } else {
      // no outlying transactions, send it
      this.submitted.emit(this.cashFlow);
    }
  }

  // TODO: call this when a new revenue/expense is pushed. Probably an event emitter from the item-table triggers this
  public recalculateTotals() {
    let totalRevenue = 0;
    let totalExpenses = 0;
    let netCashFlow = 0;

    this.cashFlow.revenueSections.forEach(revSection => {
      revSection.categories.forEach(category => {
        category.total = 0;
        category.items.forEach(i => {
          totalRevenue += i.value
          category.total += i.value;
        });
        category.items = [...category.items];
      });
      revSection.categories = [...revSection.categories];
    });

    this.cashFlow.expenseSections.forEach(expenseSection => {
      expenseSection.categories.forEach(category => {
        category.total = 0;
        category.items.forEach(i => {
          totalExpenses += i.value
          category.total += i.value;
        });
        category.items = [...category.items]
      });
      expenseSection.categories = [...expenseSection.categories];
    });
    this.cashFlow.totalRevenue = totalRevenue;
    this.cashFlow.totalExpenses = totalExpenses;
    this.cashFlow.netCashFlow = totalRevenue - totalExpenses;
    this.cashFlow.title = this.getTitle(this.cashFlow);
    this.cashFlow.uncategorizedRevenue = this.uncategorizedRevenues;
    this.cashFlow.uncategorizedExpenses = this.uncategorizedExpenses;
    if (this.cacheChanges) {
      this.localStorageService.saveItem('createCashFlow', this.cashFlow);
    }
  }

  private getTitle(t: CashFlow) {
    return `Cash Flow From ${this.formatDate(t.startDate.toDate())} to ${this.formatDate(t.endDate.toDate())}`
  }

  private formatDate(date: Date) {
    let day: string | number = date.getDate();
    let month: string | number = date.getMonth() + 1; // January is 0!
    let year = date.getFullYear();

    // ensure double digits
    day = day < 10 ? '0' + day : day;
    month = month < 10 ? '0' + month : month;

    return month + '/' + day + '/' + year;
  }
  getPageTitle() {

  }

  getNextLabel() {

  }

  getSummaryText(sectionType: 'revenue' | 'expense', section: StatementSection, i: number): string {
    let total = 0;
    if (sectionType === 'revenue') {
      this.cashFlow.revenueSections[i].categories.forEach(cat => total += cat.total);
    } else {
      this.cashFlow.expenseSections[i].categories.forEach(cat => total += cat.total);
    }
    return `Total ${section.name}: ${this.currencyService.formatNumber(total)}`;
  }

  getTotalText() {
    if (this.activeSection) {
      let total = this.activeSection?.categories
        .map(category => category.total)
        .reduce((acc, val) => acc + val, 0) as number;

      return `Total ${this.activeSection?.name}: ${this.currencyService.formatNumber(total)}`;
    }
    return `Net Cash Flow: ${this.currencyService.formatNumber(this.cashFlow.netCashFlow)}`;
  }

  dateRangeValidator(control: FormGroup): ValidationErrors | null {
    const startDate = control.get('startDate');
    const endDate = control.get('endDate');

    if (!startDate || !endDate) {
      return null;
    }

    if (startDate.value >= endDate.value) {
      return { 'dateRange': 'Start date should be before end date' };
    }
    return null;
  };

  onSelectedChange($event: StatementItem[], type: 'revenue' | 'expense') {
    if (type === 'revenue') {
      this.selectedRevenues = $event;
    } else {
      this.selectedExpenses = $event;
    }
  }

  // TODO why tf are move/delete two separate methods they're basically the same

  onMove(selectedCategory: string | undefined, type: 'revenue' | 'expense') {
    if (type === 'revenue') {
      if (selectedCategory) {
        let category = this.cashFlow.revenueSections[0].categories.filter(cat => cat.name === selectedCategory)[0];
        let catIndex = this.cashFlow.revenueSections[0].categories.indexOf(category);
        this.selectedRevenues.forEach(rev => {
          this.cashFlow.revenueSections[0].categories[catIndex].items.push(rev);
          this.cashFlow.revenueSections[0].categories[catIndex].items = [...this.cashFlow.revenueSections[0].categories[catIndex].items];
          this.cashFlow.revenueSections[0].categories = [...this.cashFlow.revenueSections[0].categories];
          let i = this.uncategorizedRevenues.indexOf(rev);
          if (i !== -1) {
            this.uncategorizedRevenues.splice(i, 1);
          }
        });
        this.selectedRevenues = [];
        this.uncategorizedRevenues = [...this.uncategorizedRevenues];
        this.recalculateTotals();
      }
    } else {
      if (selectedCategory) {
        let category = this.cashFlow.expenseSections[this.currentPageNumber - 1].categories.filter(cat => cat.name === selectedCategory)[0];
        this.selectedExpenses.forEach(exp => {
          category.items.push(exp);
          category.items = [...category.items];
          let i = this.uncategorizedExpenses.indexOf(exp);
          if (i !== -1) {
            this.uncategorizedExpenses.splice(i, 1);
          }
        });
        this.selectedExpenses = [];
        this.uncategorizedExpenses = [...this.uncategorizedExpenses];
        this.recalculateTotals();
      }
    }

    this.resetSelectedRevEmitter.emit();
    this.resetSelectedExpEmitter.emit();
    this.cdr.detectChanges();
  }

  onDelete(type: 'revenue' | 'expense') {
    if (type === 'revenue') {
      this.selectedRevenues.forEach(rev => {
        let i = this.uncategorizedRevenues.indexOf(rev);
        if (i !== -1) {
          this.uncategorizedRevenues.splice(i, 1);
        }
      });
      this.selectedRevenues = [];
      this.uncategorizedRevenues = [...this.uncategorizedRevenues];
      this.recalculateTotals();
    } else {
      this.selectedExpenses.forEach(exp => {
        let i = this.uncategorizedExpenses.indexOf(exp);
        if (i !== -1) {
          this.uncategorizedExpenses.splice(i, 1);
        }
      });
      this.selectedExpenses = [];
      this.uncategorizedExpenses = [...this.uncategorizedExpenses];
      this.recalculateTotals();
    }
  }

  private cacheFileName(name: string) {
    if (!this.localStorageService.getItem('uploadedTransactionNames')) {
      this.localStorageService.saveItem('uploadedTransactionNames', [name]);
    } else {
      this.localStorageService.getItem('uploadedTransactionNames').push(name);
    }
  }

  moveToUncategorized($event: StatementItem, cat: StatementCategory) {
    let i = cat.items.indexOf($event);
    if (i !== -1) {
      cat.items.splice(i, 1);
      cat.items = [...cat.items];
    }
    if (this.currentPageNumber < this.cashFlow.revenueSections.length) {
      this.uncategorizedRevenues.push({...$event, date: new Timestamp($event.date?.seconds!, $event.date!.nanoseconds!)});
      this.uncategorizedRevenues = [...this.uncategorizedRevenues];
    } else {
      this.uncategorizedExpenses.push({...$event, date: new Timestamp($event.date?.seconds!, $event.date!.nanoseconds!)});
      this.uncategorizedExpenses = [...this.uncategorizedExpenses];
    }
    this.recalculateTotals();
  }

  handleActionFromUncategorized(type: 'revenue' | 'expense', $event: { itemIndices: number[]; action: "move" | "delete" | "convert"; target?: string }) {
    let sourceList: StatementItem[];
    let newItems: StatementItem[] = [];

    // Determine the source list
    if (type === 'revenue') {
      sourceList = this.uncategorizedRevenues;
    } else if (type === 'expense') {
      sourceList = this.uncategorizedExpenses;
    } else {
      console.error('Invalid type specified');
      return;
    }

    // Sort indices in descending order to avoid shifting issues when removing items
    const sortedIndices = $event.itemIndices.sort((a, b) => b - a);

    for (const index of sortedIndices) {
      if (index >= 0 && index < sourceList.length) {
        const item = sourceList[index];
        newItems.push(item);
        sourceList.splice(index, 1);
      }
    }

    if ($event.action === 'move' && $event.target) {
      // Move logic: Find the target category in the active section
      const targetCategory = this.activeSection?.categories.find(cat => cat.name === $event.target);

      if (targetCategory) {
        targetCategory.items.push(...newItems);
        this.recalculateTotals();
      } else {
        console.error(`Target category "${$event.target}" not found in active section`);
      }

    } else if ($event.action === 'convert') {
      // Convert logic: Move items to the opposite uncategorized list
      if (type === 'revenue') {
        this.uncategorizedExpenses = [...this.uncategorizedExpenses, ...newItems];
      } else {
        this.uncategorizedRevenues = [...this.uncategorizedRevenues, ...newItems];
      }

      this.recalculateTotals();
    } else if ($event.action === 'delete') {
      // Delete logic: Items are already removed from the sourceList
      this.recalculateTotals();
    }

    // Update the original list
    if (type === 'revenue') {
      this.uncategorizedRevenues = [...sourceList];
    } else {
      this.uncategorizedExpenses = [...sourceList];
    }
  }

}
